import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "America/Vancouver", "America/Mexico_City",
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Berlin", "Europe/Madrid", "Europe/Amsterdam",
  "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "Australia/Sydney", "Pacific/Auckland",
  "UTC",
];

export default function AlertSettingsPage() {
  const [tz, setTz] = useState("America/New_York");
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/alert-settings").then(({ data }) => {
      setTz(data.timezone || "America/New_York");
      setEmails(data.emails || []);
    }).finally(() => setLoading(false));
  }, []);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return toast.error("Please enter a valid email.");
    if (emails.some((x) => x.email.toLowerCase() === e)) return toast.error("That email is already on the list.");
    setEmails([...emails, { email: e, enabled: true }]);
    setNewEmail("");
  };

  const toggle = (i, v) => setEmails(emails.map((e, idx) => (idx === i ? { ...e, enabled: v } : e)));
  const remove = (i) => setEmails(emails.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/alert-settings", { timezone: tz, emails });
      toast.success("Alert settings saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const activeCount = emails.filter((e) => e.enabled).length;

  return (
    <div className="space-y-6" data-testid="alert-settings-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Settings</p>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Alert Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Send an End-of-Call report to your team the moment every call ends. Recording attached as a WAV file.
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Timezone for email timestamps</Label>
          <Select value={tz} onValueChange={setTz}>
            <SelectTrigger data-testid="alert-timezone-select" className="max-w-sm mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold">Alert emails</h3>
              <p className="text-xs text-muted-foreground">{activeCount} of {emails.length} active</p>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
              placeholder="team@example.com"
              data-testid="alert-new-email-input"
              className="h-10"
            />
            <Button onClick={addEmail} data-testid="alert-add-email-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add</Button>
          </div>

          <div className="space-y-2">
            {loading && <p className="text-sm text-muted-foreground italic">Loading…</p>}
            {!loading && emails.length === 0 && (
              <p className="text-sm text-muted-foreground italic py-4">No alert emails yet. Add one above to start receiving End-of-Call reports.</p>
            )}
            {emails.map((e, i) => (
              <div
                key={e.email}
                className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3"
                data-testid={`alert-email-row-${i}`}
              >
                <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0 font-mono text-sm truncate">{e.email}</div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch
                    checked={e.enabled}
                    onCheckedChange={(v) => toggle(i, v)}
                    data-testid={`alert-email-toggle-${i}`}
                  />
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-8">{e.enabled ? "On" : "Off"}</span>
                  <Button variant="ghost" size="sm" onClick={() => remove(i)} data-testid={`alert-email-remove-${i}`} className="text-destructive h-8 w-8 p-0">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2 border-t border-border">
          <Button onClick={save} disabled={saving} data-testid="alert-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
